import { useEffect, useRef } from "react";
import { Box } from "@mui/material";

const INK = "#12100d";

/* 玄関背景A — 夜の参道: 鳥居の影・夜霧・立ち上る灯火 */
export default function ApproachA() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0, h = 0, raf = 0;

    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    // 灯火（ゆっくり立ち上る光の粒）
    const N = 42;
    const motes = Array.from({ length: N }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: 0.6 + Math.random() * 1.8,
      v: 0.008 + Math.random() * 0.02,
      sway: 8 + Math.random() * 22,
      phase: Math.random() * Math.PI * 2,
      tw: 0.5 + Math.random() * 1.2,
      warm: Math.random() < 0.8,
      a: 0.25 + Math.random() * 0.5,
    }));

    let last = performance.now();
    const draw = (now) => {
      const dt = Math.min((now - last) / 1000, 0.1);
      last = now;
      const t = now / 1000;
      ctx.clearRect(0, 0, w, h);
      for (const m of motes) {
        if (!reduced) {
          m.y -= m.v * dt;
          if (m.y < -0.05) { m.y = 1.05; m.x = Math.random(); }
        }
        const x = m.x * w + Math.sin(t * 0.4 + m.phase) * m.sway;
        const y = m.y * h;
        const tw = 0.55 + 0.45 * Math.sin(t * m.tw + m.phase);
        const alpha = m.a * tw;
        const color = m.warm ? "212,180,110" : "222,215,198";
        const g = ctx.createRadialGradient(x, y, 0, x, y, m.r * 6);
        g.addColorStop(0, `rgba(${color},${alpha})`);
        g.addColorStop(1, `rgba(${color},0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, m.r * 6, 0, Math.PI * 2);
        ctx.fill();
      }
      if (!reduced) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: -1, overflow: "hidden", bgcolor: INK }}>
      {/* 基調グラデーション */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(ellipse 90% 55% at 50% 28%, #2b2620 0%, #1b1815 48%, ${INK} 100%)`,
        }}
      />

      {/* 鳥居の影（巨大・かすか） */}
      <Box
        component="svg"
        viewBox="0 0 100 110"
        preserveAspectRatio="xMidYMax meet"
        sx={{
          position: "absolute",
          left: "50%",
          bottom: "-6vh",
          transform: "translateX(-50%)",
          height: "124vh",
          width: "auto",
          opacity: 0.6,
          filter: "blur(3px)",
        }}
      >
        <defs>
          <radialGradient id="toriiGlowA" cx="50%" cy="30%" r="55%">
            <stop offset="0%" stopColor="rgba(212,180,110,0.10)" />
            <stop offset="100%" stopColor="rgba(212,180,110,0)" />
          </radialGradient>
        </defs>
        <rect x="0" y="0" width="100" height="110" fill="url(#toriiGlowA)" />
        <g fill="rgba(210,196,168,0.028)">
          <path d="M6,12 Q50,3.5 94,12 L94,17.5 Q50,9.5 6,17.5 Z" fill="rgba(212,180,110,0.05)" />
          <rect x="11" y="18.5" width="78" height="3.6" />
          <rect x="15" y="33" width="70" height="3.8" />
          <rect x="47.6" y="22.1" width="4.8" height="10.9" />
          <path d="M19.5,20 L26,20 L28.5,110 L20.5,110 Z" />
          <path d="M80.5,20 L74,20 L71.5,110 L79.5,110 Z" />
        </g>
      </Box>

      {/* 夜霧（ゆっくり漂う） */}
      <Box
        sx={{
          position: "absolute",
          left: "-30%",
          bottom: "-12%",
          width: "120%",
          height: "46%",
          background: "radial-gradient(ellipse 55% 60% at 50% 60%, rgba(222,215,198,0.05), rgba(222,215,198,0) 70%)",
          filter: "blur(8px)",
          animation: "kodoMist1 46s ease-in-out infinite alternate",
          "@keyframes kodoMist1": {
            "0%": { transform: "translateX(0) translateY(0)" },
            "100%": { transform: "translateX(22%) translateY(-4%)" },
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: "-35%",
          bottom: "-6%",
          width: "110%",
          height: "38%",
          background: "radial-gradient(ellipse 55% 60% at 50% 55%, rgba(212,180,110,0.035), rgba(212,180,110,0) 70%)",
          filter: "blur(10px)",
          animation: "kodoMist2 58s ease-in-out infinite alternate",
          "@keyframes kodoMist2": {
            "0%": { transform: "translateX(0)" },
            "100%": { transform: "translateX(-26%)" },
          },
        }}
      />

      {/* 灯火の粒（canvas） */}
      <Box component="canvas" ref={canvasRef} sx={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />

      {/* 手前の暗がり（文字の可読性） */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(ellipse 120% 90% at 50% 115%, rgba(0,0,0,0.5), rgba(0,0,0,0) 55%)",
        }}
      />
    </Box>
  );
}
